import throttle from 'lodash/throttle';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export type Context = {
  isLarge: boolean,
  isLargeKioskScreen: boolean,
  isTablet: boolean,
  isLargeTablet: boolean,
  isMobile: boolean,
} | null;

type Props = {
  children: ReactNode,
};

const isMobileDevice = () => window.innerWidth <= 575;
const isTableDevice = () => window.innerWidth >= 576;
const isLargeTabletDevice = () => window.innerWidth >= 768;
const isLargeDevice = () => window.innerWidth >= 992;
const isLargeScreenKiosk = () =>
  window.innerWidth === 1080 && window.innerHeight <= 1920 && window.matchMedia('(orientation: portrait)').matches;

export const DeviceSizeContext = createContext<Context>(null);

export const DeviceSizeProvider = ({ children }: Props) => {
  const [isMobile, handleIsMobile] = useState(isMobileDevice());
  const [isLarge, handleIsLarge] = useState(isLargeDevice());
  const [isTablet, handleIsTablet] = useState(isTableDevice());
  const [isLargeTablet, handleIsLargeTablet] = useState(isLargeTabletDevice());
  const [isLargeKioskScreen, handleIsLargeScreenKiosk] = useState(isLargeScreenKiosk());

  const handleWindowResize = () => {
    handleIsMobile(isMobileDevice());
    handleIsLarge(isLargeDevice());
    handleIsTablet(isTableDevice());
    handleIsLargeTablet(isLargeTabletDevice());
    handleIsLargeScreenKiosk(isLargeScreenKiosk());
  };

  const throttledHandle = throttle(handleWindowResize, 200);

  useEffect(() => {
    window.addEventListener('resize', throttledHandle);

    return () => {
      window.removeEventListener('resize', throttledHandle);
    };
  }, [throttledHandle]);

  const value = useMemo(
    () => ({ isMobile, isLarge, isTablet, isLargeTablet, isLargeKioskScreen }),
    [isMobile, isLarge, isTablet, isLargeTablet, isLargeKioskScreen]
  );

  return <DeviceSizeContext.Provider value={value}>{children}</DeviceSizeContext.Provider>;
};

export const useDeviceSize = () => useContext(DeviceSizeContext);
